<script setup lang="ts">
import { faBadgeCheck, faUser } from '@fortawesome/pro-regular-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { type UserCredential, UserType } from '@store/auth/types';

const emit = defineEmits<{
    submit: [];
}>();
const authStore = useAuthStore();
const analyticsStore = useAnalyticsStore();
onMounted(() => {
    analyticsStore.trackAll('account_switch_view', { source: 'login' });
});
const { userInfo } = classToRefs(authStore);
const credentials = computed<
    (UserCredential & {
        icon?: IconDefinition;
    })[]
>(() =>
    (authStore.credentials || []).map(credential => ({
        ...credential,
        icon: getCredentialIcon(credential),
        title: getCredentialTitle(credential),
    }))
);

const getCredentialIcon = (credential: UserCredential) => {
    switch (credential.type) {
        case UserType.Organization:
            return faBadgeCheck;
        case UserType.User:
            return faUser;
        default:
            return undefined;
    }
};
const getCredentialTitle = (credential: UserCredential) => {
    switch (credential.type) {
        case UserType.User:
            return credential.title;
        case UserType.Organization:
            return `شرکت ${credential.title}`;
        default:
            return '';
    }
};
const submit = (index: number) => {
    const attrs = {
        source: 'login',
        accountType: authStore.credentials?.[index]?.type,
    };
    analyticsStore.trackAll('account_switch_select', attrs);
    authStore
        .selectAccount(index)
        .then(() => {
            analyticsStore.trackAll('account_switch_result', {
                ...attrs,
                status:
                    authStore.loggedIn &&
                    authStore.selectedCredentialIndex === index
                        ? 'success'
                        : 'failed',
            });
            if (!authStore.loggedIn) {
                return;
            }
            window.WebAppAuthChannel?.postMessage(
                userInfo.value?.userGroup?.toString() ?? ''
            );
            emit('submit');
        })
        .catch(e => {
            analyticsStore.trackAll('account_switch_result', {
                ...attrs,
                status: 'failed',
            });
            reportException(e);
        });
};
</script>

<template>
    <div class="login-popup-step">
        <div class="login-popup-text">با کدام حساب کاربری وارد می‌شوید؟</div>
        <div class="actions">
            <mr-button
                v-for="(credential, index) in credentials"
                :key="index"
                class="switch-account-btn"
                full-width
                :right-icon="credential.icon"
                @click="submit(index)"
            >
                {{ credential.title }}
            </mr-button>
        </div>
    </div>
</template>
<style lang="scss" scoped>
.login-popup-step {
    width: 373px;
    padding-inline: 20px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    flex-grow: 1;
    justify-content: center;
    @include mobile-only {
        gap: 12px;
        width: 100%;
        padding-inline: 12px;
    }
    .login-popup-text {
        @include tg-m($color: usePalette($p-cool-gray, $p-800));
    }
    .actions {
        @include mobile-only {
            padding: 24px;
            position: fixed;
            bottom: 0;
            right: 0;
            width: 100%;
        }
        display: flex;
        flex-direction: column;
        gap: 12px;
        @include mobile-only {
            padding-block: 12px;
        }
    }
}
</style>
